import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, AlertCircle, CheckCircle, UserPlus, Ban, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { formatPhoneNumber } from '../../pages/ClientIntake';
import { PersonInput } from './PersonSelect';

export default function WillsStep({ form, updateForm, addPersonToList }) {
    const [expandedSections, setExpandedSections] = useState({
        children: true,
        primaryBeneficiaries: true,
        contingentBeneficiaries: true,
        executor: true,
        distribution: true
    });
    
    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };
    
    // Children functions
    function addChild() { 
        updateForm({ children: [...form.children, { id: String(Date.now()), name: '', date_of_birth: '', relationship: 'child', minor: false, special_needs: false, disinherited: false }] }); 
    }
    function updateChild(i, f, v) { 
        const a = [...form.children]; 
        a[i] = { ...a[i], [f]: v }; 
        updateForm({ children: a }); 
    }
    function removeChild(i) { 
        updateForm({ children: form.children.filter((_, j) => j !== i) }); 
    }
    
    // Beneficiary functions
    function addBeneficiary(isContingent = false) {
        updateForm({ beneficiaries: [...form.beneficiaries, { id: String(Date.now()), name: '', relationship: '', percentage: 0, contingent: isContingent }] });
    }
    function addFromChild(child, isContingent = false) {
        const alreadyAdded = form.beneficiaries.some(b => b.name === child.name && b.relationship === 'Child' && b.contingent === isContingent);
        if (alreadyAdded) return;
        updateForm({ 
            beneficiaries: [...form.beneficiaries, { 
                id: String(Date.now()), 
                name: child.name, 
                relationship: 'Child', 
                percentage: 0, 
                contingent: isContingent 
            }] 
        });
    }
    function addSpouseAsBeneficiary(isContingent = false) {
        const alreadyAdded = form.beneficiaries.some(b => b.name === form.spouse.name && b.relationship === 'Spouse' && b.contingent === isContingent);
        if (alreadyAdded) return;
        updateForm({ 
            beneficiaries: [...form.beneficiaries, { 
                id: String(Date.now()), 
                name: form.spouse.name, 
                relationship: 'Spouse', 
                percentage: 0, 
                contingent: isContingent 
            }] 
        });
    }
    function addFormerSpouseAsBeneficiary(formerSpouse, isContingent = false) {
        const alreadyAdded = form.beneficiaries.some(b => b.name === formerSpouse.name && b.relationship === 'Former Spouse' && b.contingent === isContingent);
        if (alreadyAdded) return;
        updateForm({ 
            beneficiaries: [...form.beneficiaries, { 
                id: String(Date.now()), 
                name: formerSpouse.name, 
                relationship: 'Former Spouse', 
                percentage: 0, 
                contingent: isContingent 
            }] 
        });
    }
    function updateBeneficiary(i, f, v) {
        const a = [...form.beneficiaries];
        a[i] = { ...a[i], [f]: v };
        updateForm({ beneficiaries: a });
    }
    function removeBeneficiary(i) {
        updateForm({ beneficiaries: form.beneficiaries.filter((_, j) => j !== i) });
    }
    
    // Executor functions
    const updateExecutor = (f, v) => updateForm({ executor: { ...form.executor, [f]: v } });
    
    // Final wishes functions
    const updateFinalWishes = (f, v) => updateForm({ final_wishes: { ...form.final_wishes, [f]: v } });
    
    const el = React.createElement;
    
    // Beneficiary calculations
    const primaryBeneficiaries = form.beneficiaries.filter(b => !b.contingent);
    const contingentBeneficiaries = form.beneficiaries.filter(b => b.contingent);
    const totalPrimaryPercentage = primaryBeneficiaries.reduce((sum, b) => sum + (parseFloat(b.percentage) || 0), 0);
    const totalContingentPercentage = contingentBeneficiaries.reduce((sum, b) => sum + (parseFloat(b.percentage) || 0), 0);
    const isPrimaryExactly100 = primaryBeneficiaries.length === 0 || totalPrimaryPercentage === 100;
    const isContingentExactly100 = contingentBeneficiaries.length === 0 || totalContingentPercentage === 100;
    const primaryRemaining = 100 - totalPrimaryPercentage;
    const contingentRemaining = 100 - totalContingentPercentage;
    
    // Family data
    const children = form.children || [];
    const formerSpouses = form.former_spouses || [];
    const hasSpouse = form.marital_status === 'married' && form.spouse?.name;
    const eligibleChildren = children.filter(c => c.name && !c.disinherited);
    const disinheritedChildren = children.filter(c => c.name && c.disinherited);
    const eligibleFormerSpouses = formerSpouses.filter(fs => fs.name && !fs.disinherited);
    const disinheritedFormerSpouses = formerSpouses.filter(fs => fs.name && fs.disinherited);
    const isChildAddedPrimary = (childName) => form.beneficiaries.some(b => b.name === childName && b.relationship === 'Child' && !b.contingent);
    const isChildAddedContingent = (childName) => form.beneficiaries.some(b => b.name === childName && b.relationship === 'Child' && b.contingent);
    const isSpouseAddedPrimary = hasSpouse && form.beneficiaries.some(b => b.name === form.spouse.name && b.relationship === 'Spouse' && !b.contingent);
    const isSpouseAddedContingent = hasSpouse && form.beneficiaries.some(b => b.name === form.spouse.name && b.relationship === 'Spouse' && b.contingent);
    const isFormerSpouseAddedPrimary = (fsName) => form.beneficiaries.some(b => b.name === fsName && b.relationship === 'Former Spouse' && !b.contingent);
    const isFormerSpouseAddedContingent = (fsName) => form.beneficiaries.some(b => b.name === fsName && b.relationship === 'Former Spouse' && b.contingent);
    const hasMinorChildren = children.some(c => c.minor);
    const disinheritedChildrenNames = disinheritedChildren.map(c => c.name).join(', ');
    const disinheritedFormerSpousesNames = disinheritedFormerSpouses.map(fs => fs.name).join(', ');

    const SectionHeader = ({ title, section, count, color = 'bg-stone-100' }) => el('div', { 
        className: `flex items-center justify-between cursor-pointer p-3 ${color} border border-stone-200 hover:bg-stone-150`,
        onClick: () => toggleSection(section)
    },
        el('div', { className: 'flex items-center gap-2' },
            el('h4', { className: 'font-serif text-lg' }, title),
            count !== undefined && el('span', { className: 'text-sm text-muted-foreground' }, '(' + count + ')')
        ),
        expandedSections[section] 
            ? el(ChevronUp, { className: 'h-5 w-5 text-muted-foreground' })
            : el(ChevronDown, { className: 'h-5 w-5 text-muted-foreground' })
    );

    // Render beneficiary row
    const renderBeneficiaryRow = (b, globalIndex) => {
        return el('div', { key: b.id, className: 'p-4 border border-stone-200 space-y-4' },
            el('div', { className: 'flex justify-between' },
                el('span', { className: 'text-sm font-medium' }, 'Beneficiary'),
                el(Button, { type: 'button', variant: 'ghost', size: 'icon', onClick: () => removeBeneficiary(globalIndex), className: 'text-destructive h-8 w-8' },
                    el(Trash2, { className: 'h-4 w-4' })
                )
            ),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Name'),
                    el(PersonInput, { 
                        value: b.name, 
                        onChange: (val) => updateBeneficiary(globalIndex, 'name', val), 
                        onAddPerson: addPersonToList,
                        people: form.people || [],
                        spouse: form.spouse,
                        children: form.children,
                        placeholder: 'Select or type name',
                        relationship: 'beneficiary'
                    })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Relationship'),
                    el(Input, { value: b.relationship, onChange: e => updateBeneficiary(globalIndex, 'relationship', e.target.value), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Percentage (%)'),
                    el(Input, { type: 'number', min: 0, max: 100, value: b.percentage, onChange: e => updateBeneficiary(globalIndex, 'percentage', parseFloat(e.target.value) || 0), className: 'rounded-none', placeholder: 'e.g., 50' })
                )
            )
        );
    };

    return el('div', { className: 'space-y-6' },
        // CHILDREN SECTION
        el('div', { className: 'border border-stone-200' },
            SectionHeader({ title: 'Children', section: 'children', count: children.length }),
            expandedSections.children && el('div', { className: 'p-4 space-y-4' },
                el('div', { className: 'flex justify-end' },
                    el(Button, { type: 'button', variant: 'outline', size: 'sm', onClick: addChild, className: 'rounded-none', 'data-testid': 'add-child-btn' },
                        el(Plus, { className: 'h-4 w-4 mr-2' }), 'Add Child'
                    )
                ),
                children.length === 0
                    ? el('p', { className: 'text-muted-foreground text-sm text-center py-4' }, 'No children added')
                    : children.map((c, i) =>
                        el('div', { key: c.id, className: 'p-4 border border-stone-200 space-y-4' + (c.disinherited ? ' border-red-300 bg-red-50/30' : '') },
                            el('div', { className: 'flex justify-between' },
                                el('span', { className: 'text-sm font-medium' }, 'Child ' + (i + 1) + (c.disinherited ? ' (Disinherited)' : '')),
                                el(Button, { type: 'button', variant: 'ghost', size: 'icon', onClick: () => removeChild(i), className: 'text-destructive h-8 w-8' },
                                    el(Trash2, { className: 'h-4 w-4' })
                                )
                            ),
                            el('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
                                el('div', { className: 'space-y-2' },
                                    el(Label, null, 'Full Name'),
                                    el(Input, { value: c.name, onChange: e => updateChild(i, 'name', e.target.value), className: 'rounded-none' })
                                ),
                                el('div', { className: 'space-y-2' },
                                    el(Label, null, 'Date of Birth'),
                                    el(Input, { type: 'date', value: c.date_of_birth, onChange: e => updateChild(i, 'date_of_birth', e.target.value), className: 'rounded-none' })
                                )
                            ),
                            el('div', { className: 'flex flex-wrap gap-6' },
                                el('div', { className: 'flex items-center space-x-2' },
                                    el(Checkbox, { id: 'minor-' + i, checked: c.minor, onCheckedChange: v => updateChild(i, 'minor', v) }),
                                    el(Label, { htmlFor: 'minor-' + i, className: 'text-sm' }, 'Minor')
                                ),
                                el('div', { className: 'flex items-center space-x-2' },
                                    el(Checkbox, { id: 'special-' + i, checked: c.special_needs, onCheckedChange: v => updateChild(i, 'special_needs', v) }),
                                    el(Label, { htmlFor: 'special-' + i, className: 'text-sm' }, 'Special Needs')
                                ),
                                el('div', { className: 'flex items-center space-x-2' },
                                    el(Checkbox, { id: 'disinherit-' + i, checked: c.disinherited, onCheckedChange: v => updateChild(i, 'disinherited', v) }),
                                    el(Label, { htmlFor: 'disinherit-' + i, className: 'text-sm text-red-600' }, 'Disinherit')
                                )
                            )
                        )
                    )
            )
        ),
        
        // PRIMARY BENEFICIARIES SECTION
        el('div', { className: 'border border-stone-200' },
            SectionHeader({ title: 'Primary Beneficiaries', section: 'primaryBeneficiaries', count: primaryBeneficiaries.length, color: 'bg-green-50' }),
            expandedSections.primaryBeneficiaries && el('div', { className: 'p-4 space-y-4' },
                // Family Quick Add
                (children.length > 0 || hasSpouse || eligibleFormerSpouses.length > 0) && el('div', { className: 'p-4 border border-blue-200 bg-blue-50/50 space-y-3' },
                    el('div', { className: 'flex items-center gap-2' },
                        el(Users, { className: 'h-5 w-5 text-blue-600' }),
                        el('span', { className: 'font-medium text-blue-800' }, 'Quick Add Family Members')
                    ),
                    el('div', { className: 'flex flex-wrap gap-2' },
                        hasSpouse && el(Button, { 
                            type: 'button', 
                            variant: isSpouseAddedPrimary ? 'secondary' : 'outline',
                            size: 'sm', 
                            onClick: () => addSpouseAsBeneficiary(false),
                            disabled: isSpouseAddedPrimary,
                            className: 'rounded-none ' + (isSpouseAddedPrimary ? 'bg-green-100 text-green-700' : '')
                        },
                            isSpouseAddedPrimary ? el(CheckCircle, { className: 'h-4 w-4 mr-2' }) : el(UserPlus, { className: 'h-4 w-4 mr-2' }),
                            form.spouse.name + ' (Spouse)'
                        ),
                        eligibleChildren.map((child, idx) => {
                            const added = isChildAddedPrimary(child.name);
                            return el(Button, { 
                                key: child.id || idx,
                                type: 'button', 
                                variant: added ? 'secondary' : 'outline',
                                size: 'sm', 
                                onClick: () => addFromChild(child, false),
                                disabled: added,
                                className: 'rounded-none ' + (added ? 'bg-green-100 text-green-700' : '')
                            },
                                added ? el(CheckCircle, { className: 'h-4 w-4 mr-2' }) : el(UserPlus, { className: 'h-4 w-4 mr-2' }),
                                child.name + ' (Child)'
                            );
                        }),
                        eligibleFormerSpouses.map((fs, idx) => {
                            const added = isFormerSpouseAddedPrimary(fs.name);
                            return el(Button, { 
                                key: fs.id || idx,
                                type: 'button', 
                                variant: added ? 'secondary' : 'outline',
                                size: 'sm', 
                                onClick: () => addFormerSpouseAsBeneficiary(fs, false),
                                disabled: added,
                                className: 'rounded-none ' + (added ? 'bg-green-100 text-green-700' : '')
                            },
                                added ? el(CheckCircle, { className: 'h-4 w-4 mr-2' }) : el(UserPlus, { className: 'h-4 w-4 mr-2' }),
                                fs.name + ' (Former Spouse)'
                            );
                        })
                    )
                ),
                
                el('div', { className: 'flex justify-end' },
                    el(Button, { type: 'button', variant: 'outline', size: 'sm', onClick: () => addBeneficiary(false), className: 'rounded-none', 'data-testid': 'add-primary-beneficiary-btn' },
                        el(Plus, { className: 'h-4 w-4 mr-2' }), 'Add Primary Beneficiary'
                    )
                ),
                
                // Percentage summary
                primaryBeneficiaries.length > 0 && el('div', { className: 'p-3 border ' + (totalPrimaryPercentage > 100 ? 'border-red-300 bg-red-50' : isPrimaryExactly100 ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50') },
                    el('div', { className: 'flex items-center justify-between' },
                        el('span', { className: 'text-sm font-medium' }, 'Total: ' + totalPrimaryPercentage.toFixed(1) + '%'),
                        el('span', { className: 'text-sm' }, isPrimaryExactly100 ? '✓ Complete' : primaryRemaining.toFixed(1) + '% remaining')
                    )
                ),
                
                primaryBeneficiaries.length === 0 
                    ? el('p', { className: 'text-muted-foreground text-sm text-center py-4' }, 'No primary beneficiaries added')
                    : primaryBeneficiaries.map((b) => {
                        const globalIndex = form.beneficiaries.findIndex(ben => ben.id === b.id);
                        return renderBeneficiaryRow(b, globalIndex);
                    })
            )
        ),
        
        // CONTINGENT BENEFICIARIES SECTION
        el('div', { className: 'border border-stone-200' },
            SectionHeader({ title: 'Contingent Beneficiaries', section: 'contingentBeneficiaries', count: contingentBeneficiaries.length, color: 'bg-amber-50' }),
            expandedSections.contingentBeneficiaries && el('div', { className: 'p-4 space-y-4' },
                el('p', { className: 'text-sm text-muted-foreground' }, 'Contingent beneficiaries receive inheritance if primary beneficiaries are unable to.'),
                
                // Family Quick Add for Contingent
                (children.length > 0 || hasSpouse || eligibleFormerSpouses.length > 0) && el('div', { className: 'p-4 border border-blue-200 bg-blue-50/50 space-y-3' },
                    el('div', { className: 'flex items-center gap-2' },
                        el(Users, { className: 'h-5 w-5 text-blue-600' }),
                        el('span', { className: 'font-medium text-blue-800' }, 'Quick Add Family Members')
                    ),
                    el('div', { className: 'flex flex-wrap gap-2' },
                        hasSpouse && el(Button, { 
                            type: 'button', 
                            variant: isSpouseAddedContingent ? 'secondary' : 'outline',
                            size: 'sm', 
                            onClick: () => addSpouseAsBeneficiary(true),
                            disabled: isSpouseAddedContingent,
                            className: 'rounded-none ' + (isSpouseAddedContingent ? 'bg-green-100 text-green-700' : '')
                        },
                            isSpouseAddedContingent ? el(CheckCircle, { className: 'h-4 w-4 mr-2' }) : el(UserPlus, { className: 'h-4 w-4 mr-2' }),
                            form.spouse.name + ' (Spouse)'
                        ),
                        eligibleChildren.map((child, idx) => {
                            const added = isChildAddedContingent(child.name);
                            return el(Button, { 
                                key: child.id || idx,
                                type: 'button', 
                                variant: added ? 'secondary' : 'outline',
                                size: 'sm', 
                                onClick: () => addFromChild(child, true),
                                disabled: added,
                                className: 'rounded-none ' + (added ? 'bg-green-100 text-green-700' : '')
                            },
                                added ? el(CheckCircle, { className: 'h-4 w-4 mr-2' }) : el(UserPlus, { className: 'h-4 w-4 mr-2' }),
                                child.name + ' (Child)'
                            );
                        }),
                        eligibleFormerSpouses.map((fs, idx) => {
                            const added = isFormerSpouseAddedContingent(fs.name);
                            return el(Button, { 
                                key: fs.id || idx,
                                type: 'button', 
                                variant: added ? 'secondary' : 'outline',
                                size: 'sm', 
                                onClick: () => addFormerSpouseAsBeneficiary(fs, true),
                                disabled: added,
                                className: 'rounded-none ' + (added ? 'bg-green-100 text-green-700' : '')
                            },
                                added ? el(CheckCircle, { className: 'h-4 w-4 mr-2' }) : el(UserPlus, { className: 'h-4 w-4 mr-2' }),
                                fs.name + ' (Former Spouse)'
                            );
                        })
                    )
                ),
                
                el('div', { className: 'flex justify-end' },
                    el(Button, { type: 'button', variant: 'outline', size: 'sm', onClick: () => addBeneficiary(true), className: 'rounded-none', 'data-testid': 'add-contingent-beneficiary-btn' },
                        el(Plus, { className: 'h-4 w-4 mr-2' }), 'Add Contingent Beneficiary'
                    )
                ),
                
                // Percentage summary
                contingentBeneficiaries.length > 0 && el('div', { className: 'p-3 border ' + (totalContingentPercentage > 100 ? 'border-red-300 bg-red-50' : isContingentExactly100 ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50') },
                    el('div', { className: 'flex items-center justify-between' },
                        el('span', { className: 'text-sm font-medium' }, 'Total: ' + totalContingentPercentage.toFixed(1) + '%'),
                        el('span', { className: 'text-sm' }, isContingentExactly100 ? '✓ Complete' : contingentRemaining.toFixed(1) + '% remaining')
                    )
                ),
                
                contingentBeneficiaries.length === 0 
                    ? el('p', { className: 'text-muted-foreground text-sm text-center py-4' }, 'No contingent beneficiaries added')
                    : contingentBeneficiaries.map((b) => {
                        const globalIndex = form.beneficiaries.findIndex(ben => ben.id === b.id);
                        return renderBeneficiaryRow(b, globalIndex);
                    })
            )
        ),
        
        // EXECUTOR SECTION
        el('div', { className: 'border border-stone-200' },
            SectionHeader({ title: 'Executor', section: 'executor' }),
            expandedSections.executor && el('div', { className: 'p-4 space-y-4' },
                el('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
                    el('div', { className: 'space-y-2 col-span-2' },
                        el(Label, null, 'Primary Executor Name'),
                        el(PersonInput, { 
                            value: form.executor.primary_name, 
                            onChange: (val) => updateExecutor('primary_name', val), 
                            onAddPerson: addPersonToList,
                            people: form.people || [],
                            spouse: form.spouse,
                            children: form.children,
                            placeholder: 'Select or type name',
                            relationship: 'executor'
                        })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Pronouns'),
                        el('select', { 
                            value: form.executor.primary_gender || '', 
                            onChange: e => updateExecutor('primary_gender', e.target.value),
                            className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none'
                        },
                            el('option', { value: '' }, 'Select...'),
                            el('option', { value: 'male' }, 'He/Him'),
                            el('option', { value: 'female' }, 'She/Her'),
                            el('option', { value: 'neutral' }, 'They/Them')
                        )
                    )
                ),
                el('div', { className: 'grid grid-cols-1 md:grid-cols-4 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Phone'),
                        el(Input, { type: 'tel', value: form.executor.primary_phone, onChange: e => updateExecutor('primary_phone', formatPhoneNumber(e.target.value)), className: 'rounded-none', placeholder: '(555) 555-5555' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Relationship'),
                        el(Input, { value: form.executor.primary_relationship, onChange: e => updateExecutor('primary_relationship', e.target.value.toLowerCase()), className: 'rounded-none', placeholder: 'e.g., daughter' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'County'),
                        el(Input, { value: form.executor.primary_county || '', onChange: e => updateExecutor('primary_county', e.target.value), className: 'rounded-none' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'State'),
                        el(Input, { value: form.executor.primary_state || '', onChange: e => updateExecutor('primary_state', e.target.value), className: 'rounded-none' })
                    )
                ),
                el('div', { className: 'border-t border-stone-200 pt-4 mt-4' },
                    el('h5', { className: 'text-sm font-medium text-muted-foreground mb-3' }, 'ALTERNATE EXECUTOR')
                ),
                el('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Alternate Name'),
                        el(PersonInput, { 
                            value: form.executor.alternate_name, 
                            onChange: (val) => updateExecutor('alternate_name', val), 
                            onAddPerson: addPersonToList,
                            people: form.people || [],
                            spouse: form.spouse,
                            children: form.children,
                            placeholder: 'Select or type name',
                            relationship: 'alternate executor'
                        })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Relationship'),
                        el(Input, { value: form.executor.alternate_relationship || '', onChange: e => updateExecutor('alternate_relationship', e.target.value.toLowerCase()), className: 'rounded-none' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Pronouns'),
                        el('select', { 
                            value: form.executor.alternate_gender || '', 
                            onChange: e => updateExecutor('alternate_gender', e.target.value),
                            className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none'
                        },
                            el('option', { value: '' }, 'Select...'),
                            el('option', { value: 'male' }, 'He/Him'),
                            el('option', { value: 'female' }, 'She/Her'),
                            el('option', { value: 'neutral' }, 'They/Them')
                        )
                    )
                ),
                el('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Alternate Phone'),
                        el(Input, { type: 'tel', value: form.executor.alternate_phone || '', onChange: e => updateExecutor('alternate_phone', formatPhoneNumber(e.target.value)), className: 'rounded-none', placeholder: '(555) 555-5555' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Alternate County'),
                        el(Input, { value: form.executor.alternate_county || '', onChange: e => updateExecutor('alternate_county', e.target.value), className: 'rounded-none' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Alternate State'),
                        el(Input, { value: form.executor.alternate_state || '', onChange: e => updateExecutor('alternate_state', e.target.value), className: 'rounded-none' })
                    )
                )
            )
        ),
        
        // DISTRIBUTION & DISPOSITION SECTION
        el('div', { className: 'border border-stone-200' },
            SectionHeader({ title: 'Distribution & Final Wishes', section: 'distribution' }),
            expandedSections.distribution && el('div', { className: 'p-4 space-y-4' },
                el('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Distribution Type'),
                        el('select', { 
                            value: form.final_wishes?.distribution_type || 'per_stirpes', 
                            onChange: e => updateFinalWishes('distribution_type', e.target.value),
                            className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none',
                            'data-testid': 'distribution-type-select'
                        },
                            el('option', { value: 'na' }, 'N/A'),
                            el('option', { value: 'per_stirpes' }, 'Per Stirpes'),
                            el('option', { value: 'per_capita' }, 'Per Capita')
                        )
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Disposition Method'),
                        el('select', { 
                            value: form.final_wishes?.disposition_method || 'cremation', 
                            onChange: e => updateFinalWishes('disposition_method', e.target.value),
                            className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none'
                        },
                            el('option', { value: 'cremation' }, 'Cremation'),
                            el('option', { value: 'burial' }, 'Burial')
                        )
                    )
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Disposition Instructions'),
                    el(Textarea, { 
                        value: form.final_wishes?.disposition_instructions || '', 
                        onChange: e => updateFinalWishes('disposition_instructions', e.target.value), 
                        className: 'rounded-none min-h-[80px]', 
                        placeholder: 'Special wishes regarding remains...',
                        'data-testid': 'disposition-instructions-textarea'
                    })
                ),
                
                // Disinheritance section
                el('div', { className: 'border-t border-stone-200 pt-4 mt-4' },
                    el('h5', { className: 'text-sm font-medium text-muted-foreground mb-3' }, 'DISINHERITANCE')
                ),
                disinheritedChildren.length > 0 && el('div', { className: 'p-3 bg-red-50 border border-red-200 flex items-start gap-2 mb-2' },
                    el(AlertCircle, { className: 'h-4 w-4 text-red-600 mt-0.5' }),
                    el('div', null,
                        el('p', { className: 'text-sm font-medium text-red-700' }, 'Disinherited children: ' + disinheritedChildrenNames)
                    )
                ),
                disinheritedFormerSpouses.length > 0 && el('div', { className: 'p-3 bg-red-50 border border-red-200 flex items-start gap-2 mb-2' },
                    el(AlertCircle, { className: 'h-4 w-4 text-red-600 mt-0.5' }),
                    el('div', null,
                        el('p', { className: 'text-sm font-medium text-red-700' }, 'Disinherited former spouses: ' + disinheritedFormerSpousesNames)
                    )
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Additional Persons to Disinherit'),
                    el(Textarea, { 
                        value: form.final_wishes?.disinherited_persons || '', 
                        onChange: e => updateFinalWishes('disinherited_persons', e.target.value), 
                        className: 'rounded-none min-h-[60px]', 
                        placeholder: 'e.g., John Doe (estranged brother)',
                        'data-testid': 'disinherited-persons-textarea'
                    })
                ),
                
                // Minor children provisions
                hasMinorChildren && el('div', { className: 'p-4 border border-amber-300 bg-amber-50/30 space-y-4 mt-4' },
                    el('h5', { className: 'font-medium text-amber-800' }, 'Minor Children Provisions'),
                    el('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
                        el('div', { className: 'space-y-2' },
                            el(Label, null, 'Preferred Guardian'),
                            el(Input, { 
                                value: form.final_wishes?.minor_children_guardian || '', 
                                onChange: e => updateFinalWishes('minor_children_guardian', e.target.value), 
                                className: 'rounded-none'
                            })
                        ),
                        el('div', { className: 'space-y-2' },
                            el(Label, null, 'Guardian Relationship'),
                            el(Input, { 
                                value: form.final_wishes?.minor_children_guardian_relationship || '', 
                                onChange: e => updateFinalWishes('minor_children_guardian_relationship', e.target.value), 
                                className: 'rounded-none'
                            })
                        )
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Additional Wishes for Minor Children'),
                        el(Textarea, { 
                            value: form.final_wishes?.minor_children_wishes || '', 
                            onChange: e => updateFinalWishes('minor_children_wishes', e.target.value), 
                            className: 'rounded-none min-h-[80px]'
                        })
                    )
                ),
                el('div', { className: 'space-y-2 mt-4' },
                    el(Label, null, 'Pet Care Instructions'),
                    el(Textarea, { 
                        value: form.final_wishes?.pet_care_instructions || '', 
                        onChange: e => updateFinalWishes('pet_care_instructions', e.target.value), 
                        className: 'rounded-none min-h-[60px]', 
                        placeholder: 'e.g., My dog Max should go to my sister Sarah...'
                    })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Unusual or Special Requests'),
                    el(Textarea, { 
                        value: form.final_wishes?.unusual_requests || '', 
                        onChange: e => updateFinalWishes('unusual_requests', e.target.value), 
                        className: 'rounded-none min-h-[60px]', 
                        placeholder: 'Any other special requests...'
                    })
                )
            )
        )
    );
}
